/**
 * The player, in the header, on every surface (§ F117, 2026-08-21).
 *
 * Jake, on the menu review: *"I don't need to see `<icon>` Jake, just the
 * player icon. And if my player is selected then I should see it across all
 * screens to know I am selected. Look at how my name is missing across all of
 * these screens."* … *"I should be able to click the player icon then choose to
 * 'change players', or 'change avatar'."*
 *
 * So: ONE icon-sized button, drawn by the CHROME rather than by each page, on
 * every screen that has a header. Present = you are signed in; absent = nobody
 * is. Pressing it opens the card, which is where the name lives and where the
 * two changes are offered.
 *
 * The face is `PlayerAvatar` — the chosen preset, the drawing they made, or the
 * generic person on their own session colour (§ F120) — so "which of us is
 * this" is answerable at a glance without the name being on the line.
 */

import { useEffect, useState, useSyncExternalStore } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Smile, Trophy, UsersRound, X } from "lucide-react";
import { signOutPlayer } from "../lib/player";
import { usePlayer } from "../lib/usePlayer";
import { PlayerAvatar } from "./PlayerAvatar";
import { fetchRoster, subscribeProfile, type AvatarValue } from "../lib/roster";
import { getAvatarDraft, subscribeAvatarDraft } from "../lib/avatar-draft";

export function PlayerChip() {
  const { player } = usePlayer();
  const [open, setOpen] = useState(false);
  /*
   * § F120 — the chip wears the player's chosen face. It is fetched rather
   * than stored with the identity because an avatar changed on one device
   * should appear on the others, and the roster is already the one place that
   * answers "what does this player look like".
   */
  const [avatar, setAvatar] = useState<AvatarValue | null>(null);
  const [tone, setTone] = useState<{ color: string | null; ink: string | null }>({
    color: null,
    ink: null,
  });
  /*
   * § F123 — while somebody is drawing on `/me`, the pen is ahead of the
   * server: the draft wins until it is saved (or the page leaves).
   */
  const draft = useSyncExternalStore(subscribeAvatarDraft, getAvatarDraft, () => null);
  const shown = draft ?? avatar;
  const navigate = useNavigate();

  useEffect(() => {
    if (!player) {
      setAvatar(null);
      setTone({ color: null, ink: null });
      return;
    }
    let live = true;
    const load = () => {
      fetchRoster()
        .then((rows) => {
          if (!live) return;
          const me = rows.find((row) => row.id === player.id);
          setAvatar(me?.avatar ?? null);
          setTone({ color: me?.color ?? null, ink: me?.ink ?? null });
        })
        .catch(() => {});
    };
    load();
    // § F122 — and again the moment the player changes their face, so the chip
    // above the picker updates while they are still looking at it.
    const stop = subscribeProfile(load);
    return () => {
      live = false;
      stop();
    };
  }, [player?.id, player?.name]);

  // Nobody has picked: the header says nothing, which is itself the signal.
  if (!player) return null;

  return (
    <>
      <button
        type="button"
        data-testid="chrome-player"
        data-player-id={player.id}
        aria-label={`Player: ${player.name}`}
        onClick={() => setOpen(true)}
        className="gtw-hicon shrink-0 rounded-block btn-press flex items-center justify-center p-0 overflow-hidden"
      >
        <PlayerAvatar playerId={player.id} avatar={shown} color={tone.color} ink={tone.ink} size={36} />
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-6"
          role="dialog"
          aria-modal="true"
          aria-label="Player"
          data-testid="player-card"
          onClick={() => setOpen(false)}
        >
          <div
            className="bg-white ink-border rounded-block-lg block-shadow-2 px-5 py-4 w-full max-w-xs flex flex-col items-center gap-3 text-center"
            onClick={(event) => event.stopPropagation()}
          >
            <PlayerAvatar
              playerId={player.id}
              avatar={shown}
              color={tone.color}
              ink={tone.ink}
              size={56}
            />
            <p
              className="font-heading font-extrabold uppercase tracking-tight text-xl leading-tight"
              data-testid="player-card-name"
            >
              {player.name}
            </p>

            {/* § F204 — Jake: *"Instead of 'Change Players' I need a proper
                logout."* Revokes this session on the server and forgets it
                here — same as /me's Sign out. The last account out lands on
                the sign-in screen. */}
            <button
              type="button"
              data-testid="player-card-signout"
              onClick={() => {
                setOpen(false);
                void signOutPlayer(player).then(() => navigate("/", { replace: true }));
              }}
              className="w-full min-h-[44px] px-4 bg-brand-tertiary font-heading font-extrabold uppercase tracking-wide text-sm ink-border rounded-block block-shadow-1 btn-press flex items-center justify-center gap-2"
            >
              <LogOut className="w-4 h-4" strokeWidth={2.5} />
              Sign out
            </button>

            {/* *"…or 'change avatar'"* — the name and the forty live together. */}
            <button
              type="button"
              data-testid="player-card-avatar"
              onClick={() => {
                setOpen(false);
                navigate(`/me?from=${encodeURIComponent(window.location.pathname)}`);
              }}
              className="w-full min-h-[44px] px-4 bg-brand-quaternary font-heading font-extrabold uppercase tracking-wide text-sm ink-border rounded-block block-shadow-1 btn-press flex items-center justify-center gap-2"
            >
              <Smile className="w-4 h-4" strokeWidth={2.5} />
              Name & avatar
            </button>

            {/* § F213 — every stat I have, overall and per game. */}
            <button
              type="button"
              data-testid="player-card-stats"
              onClick={() => {
                setOpen(false);
                navigate("/stats");
              }}
              className="w-full min-h-[44px] px-4 bg-white font-heading font-extrabold uppercase tracking-wide text-sm ink-border rounded-block block-shadow-1 btn-press flex items-center justify-center gap-2"
            >
              <Trophy className="w-4 h-4" strokeWidth={2.5} />
              My stats
            </button>

            {/* § F201 — the groups you are in, and the invite link. */}
            <button
              type="button"
              data-testid="player-card-groups"
              onClick={() => {
                setOpen(false);
                navigate("/groups");
              }}
              className="w-full min-h-[44px] px-4 bg-brand-primary font-heading font-extrabold uppercase tracking-wide text-sm ink-border rounded-block block-shadow-1 btn-press flex items-center justify-center gap-2"
            >
              <UsersRound className="w-4 h-4" strokeWidth={2.5} />
              My groups
            </button>

            <button
              type="button"
              data-testid="player-card-close"
              onClick={() => setOpen(false)}
              className="w-full min-h-[40px] px-4 bg-white font-heading font-extrabold uppercase tracking-wide text-sm ink-border rounded-block btn-press flex items-center justify-center gap-2"
            >
              <X className="w-4 h-4" strokeWidth={2.5} />
              Close
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default PlayerChip;
